import { useState, useMemo } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Check, AlertTriangle, Edit2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import ReAddHoldingDialog from "@/components/benchmark/ReAddHoldingDialog";

interface CbamFund {
  id: string;
  fe_fund_name: string;
  fund_manager: string | null;
  fund_name: string;
  fund_class: string | null;
  isin: string | null;
  ocf_pct: number | null;
  is_current: boolean;
}

interface FundWeight {
  id: string;
  fund_id: string;
  portfolio: number;
  weight_pct: number;
  effective_date: string;
}

interface SectorMapping {
  id: string;
  fund_id: string;
  clarification_number: number;
  sector_id: string;
  weighting: number;
}

interface MasterSector {
  id: string;
  name: string;
}

export default function FundManagement() {
  const queryClient = useQueryClient();
  const [showAddFund, setShowAddFund] = useState(false);
  const [editingFund, setEditingFund] = useState<string | null>(null);
  const [newFund, setNewFund] = useState({ fund_name: "", fe_fund_name: "", fund_manager: "", isin: "" });

  const { data: funds } = useQuery({
    queryKey: ["ba-cbam-funds-all", "v3-with-ocf"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_cbam_funds")
        .select("id, fe_fund_name, fund_manager, fund_name, fund_class, isin, ocf_pct, is_current")
        .order("fund_name");
      if (error) throw error;
      return data as CbamFund[];
    },
    staleTime: 0,
  });

  const { data: weights } = useQuery({
    queryKey: ["ba-fund-weights-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_cbam_fund_weights")
        .select("*")
        .order("portfolio");
      if (error) throw error;
      return data as FundWeight[];
    },
  });

  const { data: mappings } = useQuery({
    queryKey: ["ba-fund-sector-mappings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_cbam_fund_sector_mappings")
        .select("*")
        .order("clarification_number");
      if (error) throw error;
      return data as SectorMapping[];
    },
  });

  const { data: sectors } = useQuery({
    queryKey: ["ba-master-sectors"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_master_sectors")
        .select("id, name")
        .order("display_order");
      if (error) throw error;
      return data as MasterSector[];
    },
  });

  const addFundMutation = useMutation({
    mutationFn: async (fund: typeof newFund) => {
      if (!fund.isin.trim()) throw new Error("ISIN is required");
      const { error } = await supabase.from("ba_cbam_funds").insert({
        fund_name: fund.fund_name,
        fe_fund_name: fund.fe_fund_name,
        fund_manager: fund.fund_manager || null,
        isin: fund.isin.trim(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ba-cbam-funds-all"] });
      setShowAddFund(false);
      setNewFund({ fund_name: "", fe_fund_name: "", fund_manager: "", isin: "" });
      toast.success("Fund added");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateWeightMutation = useMutation({
    mutationFn: async ({ fund_id, portfolio, weight_pct }: { fund_id: string; portfolio: number; weight_pct: number }) => {
      const { error } = await supabase
        .from("ba_cbam_fund_weights")
        .upsert(
          { fund_id, portfolio, weight_pct, effective_date: new Date().toISOString().split("T")[0] },
          { onConflict: "fund_id,portfolio,effective_date" }
        );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ba-fund-weights-all"] });
      queryClient.invalidateQueries({ queryKey: ["ba-fund-weights-check"] });
    },
  });

  const removeFundMutation = useMutation({
    mutationFn: async (fundId: string) => {
      // Exit the fund by dating it out, never by deleting. ba_cbam_fund_weights is
      // effective-dated and every historical reconstruction reads "the latest row per fund on
      // or before date X" (src/lib/blendedPortfolioMatrix.ts), so a DELETE erases the fund's
      // whole opening position. Deleting the ba_cbam_funds row is worse — its FK from
      // ba_cbam_fund_weights is ON DELETE CASCADE. Writing weight_pct = 0 at today's date is
      // the schema's exit convention: the latest weight becomes 0, so the fund drops out of
      // this table (which filters on latest weight > 0) and trg_sync_is_current clears
      // ba_cbam_funds.is_current unless the fund is still held in another range.
      const today = new Date().toISOString().split("T")[0];
      const { error: wErr } = await supabase
        .from("ba_cbam_fund_weights")
        .upsert(
          [1, 2, 3, 4, 5].map((portfolio) => ({ fund_id: fundId, portfolio, weight_pct: 0, effective_date: today })),
          { onConflict: "fund_id,portfolio,effective_date" }
        );
      if (wErr) throw wErr;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ba-cbam-funds-all"] });
      queryClient.invalidateQueries({ queryKey: ["ba-fund-weights-all"] });
      queryClient.invalidateQueries({ queryKey: ["ba-fund-sector-mappings"] });
      toast.success("Fund removed from P range");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateMappingMutation = useMutation({
    mutationFn: async ({ fund_id, clarification_number, sector_id, weighting }: { fund_id: string; clarification_number: number; sector_id: string; weighting: number }) => {
      const { error } = await supabase
        .from("ba_cbam_fund_sector_mappings")
        .upsert(
          { fund_id, clarification_number, sector_id, weighting },
          { onConflict: "fund_id,clarification_number" }
        );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ba-fund-sector-mappings"] });
      toast.success("Mapping updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Latest-effective weight per (fund_id, portfolio)
  const latestWeights = useMemo(() => {
    const map = new Map<string, { weight: number; date: string }>();
    (weights || []).forEach((w) => {
      const key = `${w.fund_id}|${w.portfolio}`;
      const prev = map.get(key);
      if (!prev || w.effective_date > prev.date) {
        map.set(key, { weight: Number(w.weight_pct) || 0, date: w.effective_date });
      }
    });
    return map;
  }, [weights]);

  const getWeight = (fundId: string, portfolio: number) =>
    latestWeights.get(`${fundId}|${portfolio}`)?.weight || 0;

  // Monotonicity check
  const isMonotonic = (fundId: string) => {
    const w = [1, 2, 3, 4, 5].map((p) => Number(getWeight(fundId, p)));
    const inc = w.every((v, i) => i === 0 || v >= w[i - 1]);
    const dec = w.every((v, i) => i === 0 || v <= w[i - 1]);
    return inc || dec;
  };

  // Per-portfolio totals + offending funds (latest-effective)
  const portfolioTotals = useMemo(() => {
    return [1, 2, 3, 4, 5].map((p) => {
      const rows = (funds || [])
        .map((f) => ({ fund: f, weight: getWeight(f.id, p) }))
        .filter((r) => r.weight > 0)
        .sort((a, b) => b.weight - a.weight);
      const total = rows.reduce((s, r) => s + r.weight, 0);
      return { portfolio: p, total, rows };
    });
  }, [funds, latestWeights]);

  const offending = portfolioTotals.filter((t) => Math.abs(t.total - 100) > 0.05);

  const getFundMappings = (fundId: string) => {
    return mappings?.filter((m) => m.fund_id === fundId) || [];
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">P Fund Holdings and Weightings</h1>
          <p className="text-sm text-muted-foreground">
            Manage CBAM fund holdings, weights, and sector mappings
          </p>
        </div>
        <div className="flex items-center gap-2">
        <ReAddHoldingDialog
          weightsTable="ba_cbam_fund_weights"
          portfolios={[1, 2, 3, 4, 5].map((n) => ({ num: n, label: `P${n}` }))}
          rangeLabel="P"
          invalidateKeys={[["ba-cbam-funds-all"], ["ba-fund-weights-all"], ["ba-fund-weights-check"]]}
        />
        <Dialog open={showAddFund} onOpenChange={setShowAddFund}>
          <DialogTrigger asChild>
            <Button size="sm"><Plus className="h-4 w-4 mr-1" /> Add Fund</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add New Fund</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Display Name</Label>
                <Input value={newFund.fund_name} onChange={(e) => setNewFund({ ...newFund, fund_name: e.target.value })} placeholder="e.g. Fidelity US Index" />
              </div>
              <div>
                <Label>FE Fund Name (exact match)</Label>
                <Input value={newFund.fe_fund_name} onChange={(e) => setNewFund({ ...newFund, fe_fund_name: e.target.value })} placeholder="e.g. Fidelity International (FIL In Index US P" />
              </div>
              <div>
                <Label>Fund Manager</Label>
                <Input value={newFund.fund_manager} onChange={(e) => setNewFund({ ...newFund, fund_manager: e.target.value })} placeholder="e.g. Fidelity" />
              </div>
              <div>
                <Label>ISIN <span className="text-destructive">*</span></Label>
                <Input value={newFund.isin} onChange={(e) => setNewFund({ ...newFund, isin: e.target.value.toUpperCase() })} placeholder="e.g. GB00B7VFLF21" maxLength={12} />
              </div>
              <p className="text-xs text-muted-foreground">
                For a fund that was previously held and sold out, use "Re-add Holding" instead —
                adding it again here fails on the unique FE fund name.
              </p>
              <Button onClick={() => addFundMutation.mutate(newFund)} disabled={!newFund.fund_name || !newFund.fe_fund_name || !newFund.isin.trim()}>
                Add Fund
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {offending.length > 0 && (
        <Alert variant="destructive" className="border-amber-500/60 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100 [&>svg]:text-amber-600">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Portfolio weights do not sum to 100%</AlertTitle>
          <AlertDescription className="space-y-3 mt-2">
            {offending.map((o) => {
              const delta = o.total - 100;
              const top = o.rows.slice(0, 5);
              return (
                <div key={o.portfolio} className="text-xs">
                  <div className="font-medium">
                    P{o.portfolio}: {o.total.toFixed(2)}% ({delta > 0 ? "+" : ""}{delta.toFixed(2)}% vs 100%) — {o.rows.length} funds
                  </div>
                  <div className="text-muted-foreground mt-1">
                    Largest holdings to review: {top.map((r) => `${r.fund.fund_manager ?? "—"} ${r.fund.fund_name} (${r.weight.toFixed(2)}%)`).join("; ")}
                  </div>
                </div>
              );
            })}
            <div className="text-[11px] opacity-80">Tolerance: ±0.05%. Nudge one or two of the largest holdings to bring the column back to 100.00%.</div>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Fund Holdings & Weights</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-3 font-medium text-muted-foreground min-w-[100px]">Manager</th>
                <th className="text-left p-3 font-medium text-muted-foreground min-w-[200px]">Fund</th>
                <th className="text-left p-3 font-medium text-muted-foreground min-w-[100px]">Fund Class</th>
                <th className="text-left p-3 font-medium text-muted-foreground min-w-[120px]">ISIN</th>
                <th className="text-center p-3 font-medium text-muted-foreground min-w-[80px]">OCF %</th>
                {[1, 2, 3, 4, 5].map((p) => (
                  <th key={p} className="text-center p-3 font-medium text-muted-foreground min-w-[80px]">P{p} %</th>
                ))}
                <th className="text-center p-3 font-medium text-muted-foreground min-w-[70px]">Test</th>
                <th className="text-center p-3 font-medium text-muted-foreground min-w-[60px]">Active</th>
                <th className="text-center p-3 font-medium text-muted-foreground min-w-[60px]">Remove</th>
              </tr>
            </thead>
            <tbody>
              {funds?.filter((fund) =>
                [1, 2, 3, 4, 5].some((p) => getWeight(fund.id, p) > 0)
              ).slice().sort((a, b) => {
                const m = (a.fund_manager || "").localeCompare(b.fund_manager || "", undefined, { sensitivity: "base" });
                if (m !== 0) return m;
                const f = (a.fund_name || "").localeCompare(b.fund_name || "", undefined, { sensitivity: "base" });
                if (f !== 0) return f;
                return (a.fund_class || "").localeCompare(b.fund_class || "", undefined, { sensitivity: "base" });
              }).map((fund, idx) => {
                const mono = isMonotonic(fund.id);
                return (
                  <tr key={fund.id} className={cn("border-b", idx % 2 === 0 ? "bg-background" : "bg-muted/20")}>
                    <td className="p-3 text-xs text-muted-foreground">{fund.fund_manager || "—"}</td>
                    <td className="p-3 text-xs font-medium">{fund.fund_name}</td>
                    <td className="p-3 text-xs text-muted-foreground">{fund.fund_class || "—"}</td>
                    <td className="p-3 text-xs font-mono text-muted-foreground">{fund.isin || <span className="text-destructive">—</span>}</td>
                    <td className="p-3 text-center text-xs font-mono text-muted-foreground">
                      {fund.ocf_pct == null ? "—" : Number(fund.ocf_pct).toFixed(2)}
                    </td>

                    {[1, 2, 3, 4, 5].map((p) => (
                      <td key={p} className="p-2 text-center">
                        <Input
                          key={`${fund.id}-${p}-${getWeight(fund.id, p)}`}
                          type="number"
                          step="0.01"
                          className="w-20 h-7 text-xs text-center mx-auto"
                          defaultValue={Number(getWeight(fund.id, p)).toFixed(2)}
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val)) {
                              updateWeightMutation.mutate({ fund_id: fund.id, portfolio: p, weight_pct: val });
                            }
                          }}
                        />
                      </td>
                    ))}
                    <td className="p-2 text-center">
                      {mono ? (
                        <Badge variant="outline" className="text-green-600 border-green-300 text-[10px]">
                          <Check className="h-3 w-3 mr-0.5" /> Fine
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="text-[10px]">
                          <AlertTriangle className="h-3 w-3 mr-0.5" /> CHECK
                        </Badge>
                      )}
                    </td>
                    <td className="p-2 text-center">
                      <Badge variant={fund.is_current ? "default" : "secondary"} className="text-[10px]">
                        {fund.is_current ? "Active" : "Legacy"}
                      </Badge>
                    </td>
                    <td className="p-2 text-center">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove {fund.fund_name} from P range?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This sets all P1–P5 weights to 0% as of today, so the fund drops out of the P range. Its weight history and sector mappings are kept, and any holding in the R, Dist or MM ranges is untouched — but if this was the only range holding it, removing it here leaves it held nowhere. Either way it can be brought back with "Re-add Holding", or by logging a BUY in the Trade Log.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => removeFundMutation.mutate(fund.id)}>Remove</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 bg-muted/60 font-medium">
                <td className="p-3 text-xs" colSpan={5}>Total</td>
                {portfolioTotals.map((t) => {
                  const ok = Math.abs(t.total - 100) < 0.05;
                  return (
                    <td key={t.portfolio} className={cn("p-3 text-center text-xs font-mono", ok ? "text-emerald-600" : "text-red-600")}>
                      {t.total.toFixed(2)}%
                    </td>
                  );
                })}
                <td colSpan={3} />
              </tr>
            </tfoot>
          </table>

        </CardContent>
      </Card>
    </div>
  );
}
